import type { TOC } from '@ember/component/template-only';
import Heading from './heading';
import Text from './text';
import type { SemanticIntent } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// CtaBanner – soft gradient call-to-action block for page bottoms
// ---------------------------------------------------------------------------

/**
 * Semantic intent for the CTA banner's gradient + text treatment. Replaces
 * the old open decorative `@theme` API (emerald / blue / violet / amber /
 * teal / rose / indigo / pink). The decorative palette collapses onto the
 * canonical brand/status recipes via the `.app-cta-banner--*` modifiers in
 * styles.css. `neutral` is excluded — a CTA always carries a brand/status
 * intent.
 */
export type CtaBannerIntent = Exclude<SemanticIntent, 'neutral'>;

interface CtaBannerSignature {
  Args: {
    /** Heading text */
    heading: string;
    /** Description text */
    description?: string;
    /** Semantic intent for the gradient background (default: 'brand') */
    intent?: CtaBannerIntent;
  };
  Blocks: {
    /** Action content (button, link) */
    action?: [];
  };
  Element: HTMLDivElement;
}

const INTENT_CLASS: Record<CtaBannerIntent, string> = {
  brand: 'app-cta-banner--brand',
  success: 'app-cta-banner--success',
  info: 'app-cta-banner--info',
  warning: 'app-cta-banner--warning',
  danger: 'app-cta-banner--danger',
};

const intentClass = (intent?: CtaBannerIntent) => INTENT_CLASS[intent ?? 'brand'];

/**
 * CtaBanner - A soft gradient call-to-action block for page bottoms
 *
 * Displays a centered heading, description, and action slot on a light
 * gradient background. Used at the bottom of feature pages to encourage
 * the user to take a next step.
 *
 * @example Basic usage
 * ```gts
 * import CtaBanner from 'tio-reimagined/components/cta-banner';
 *
 * <CtaBanner
 *   @heading="Balance Student Loans & Retirement"
 *   @description="Our optimizer helps you allocate benefits for maximum impact."
 *   @intent="success"
 * >
 *   <:action>
 *     <LinkTo @route="authenticated.reimagined.finance.optimizer">
 *       <Button @intent="primary">Optimize My Benefits</Button>
 *     </LinkTo>
 *   </:action>
 * </CtaBanner>
 * ```
 */
const CtaBanner: TOC<CtaBannerSignature> = <template>
  <div data-test-cta-banner class="app-cta-banner {{intentClass @intent}}" ...attributes>
    <Heading
      data-test-cta-banner-heading
      @level={{3}}
      class="app-cta-banner-heading text-xl font-bold mb-2"
    >
      {{@heading}}
    </Heading>
    {{#if @description}}
      <Text
        data-test-cta-banner-description
        @variant="inherit"
        class="app-cta-banner-description mb-6"
      >
        {{@description}}
      </Text>
    {{/if}}
    {{#if (has-block "action")}}
      <div data-test-cta-banner-action>
        {{yield to="action"}}
      </div>
    {{/if}}
  </div>
</template>;

export default CtaBanner;

export type { CtaBannerSignature };
